import React, { useEffect, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import MobileNavigation from "../../components/ui/MobileNavigation";
import Breadcrumb from "../../components/ui/Breadcrumb";
import { Phone, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const plans = [
  { name: "Free", price: 0, duration: "Forever", highlight: false },
  { name: "Basic", price: 499, duration: "Per Month", highlight: false },
  { name: "Pro", price: 2499, duration: "6 Months", highlight: true },
  { name: "Enterprise", price: 4499, duration: "Yearly", highlight: false },
];

const SubscriptionPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const data = localStorage.getItem("user");
    const token = sessionStorage.getItem("token");

    if (!data || !token) {
      navigate("/whats-app-authentication");
      return;
    }

    const parsed = JSON.parse(data);
    setUser(parsed);
  }, [navigate]);

  const currentPlan = user?.vendorDetails?.subscription?.plan || "free";
  const endDate = user?.vendorDetails?.subscription?.endDate;
  const planStatus = user?.vendorDetails?.subscription?.status;

  const handleSidebarToggle = () => setSidebarCollapsed(!sidebarCollapsed);
  const handleMobileNavToggle = () => setMobileNavOpen(!mobileNavOpen);

  const handleUpgrade = (planName) => {
    toast.success(`Upgrade request for ${planName} plan submitted!`);
    // Future: redirect to Razorpay checkout
  };

  const handleContact = (type) => {
    if (type === "call") {
      window.location.href = "tel:+6596261627";
    } else {
      window.location.href = "mailto:support@worklycrm.store";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        onSidebarToggle={handleMobileNavToggle}
        sidebarCollapsed={sidebarCollapsed}
      />
      <Sidebar
        user={user}
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />
      <MobileNavigation
        user={user}
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      <main
        className={`pt-16 transition-all duration-200 ${
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-60"
        }`}
      >
        <div className="p-6 space-y-6">
          <Breadcrumb user={user} />

          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Subscription Plans
              </h1>
              <p className="text-muted-foreground">
                Choose the plan that best fits your business needs.
              </p>
            </div>
            {currentPlan && (
              <div className="mt-3 lg:mt-0 text-sm text-muted-foreground">
                <span className="font-medium text-primary">
                  Current Plan: {currentPlan?.toUpperCase()}
                </span>
                {endDate && (
                  <>
                    {" "}
                    • Expires on{" "}
                    <span className="font-semibold">
                      {new Date(endDate).toLocaleDateString()}
                    </span>
                  </>
                )}
                {" • "}
                <span
                  className={`${
                    planStatus === "active"
                      ? "text-green-500"
                      : planStatus === "trial"
                      ? "text-yellow-500"
                      : "text-red-500"
                  } font-semibold`}
                >
                  {planStatus?.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Plan Cards - Simplified */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl border bg-card shadow-sm p-6 text-center transition-transform hover:-translate-y-1 ${
                  plan.highlight ? "border-primary shadow-md" : "border-border"
                }`}
              >
                <h2 className="text-xl font-semibold mb-2 text-foreground">
                  {plan.name}
                </h2>
                <p className="text-3xl font-bold text-primary mb-1">
                  ₹{plan.price}
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  {plan.duration}
                </p>

                {currentPlan === plan.name.toLowerCase() ? (
                  <button
                    disabled
                    className="w-full py-2 bg-muted text-muted-foreground rounded-md cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan.name)}
                    className={`w-full py-2 rounded-md text-white ${
                      plan.highlight
                        ? "bg-primary hover:bg-primary/90"
                        : "bg-gray-600 hover:bg-gray-700"
                    }`}
                  >
                    Upgrade
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Contact Support */}
          <div className="mt-10 bg-card border border-border rounded-2xl p-6 shadow-sm text-center">
            <h2 className="text-xl font-bold text-foreground mb-2">
              Need Assistance?
            </h2>
            <p className="text-muted-foreground mb-4">
              Have questions about your plan or need a custom subscription?
              Contact our support team.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={() => handleContact("call")}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition"
              >
                <Phone className="w-4 h-4" />
                <span>Call Support</span>
              </button>

              <button
                onClick={() => handleContact("email")}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition"
              >
                <Mail className="w-4 h-4" />
                <span>Email Support</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
